import { Plus } from '@tamagui/lucide-icons-2'
import { useUsersApi } from 'apiProviders'
import { useUserData } from 'auth'
import Button from 'components/button'
import SectionHead from 'components/sectionHead'
import { useSearchParamState } from 'helperFunctions/react'
import { useNavigate } from 'react-router'
import { SizableText, View, XStack } from 'tamagui'
import AgenciesPanel from './agenciesPanel'
import MembersList from './membersList'
import RolesPanel from './rolesPanel'

type SubTab = 'members' | 'roles' | 'agencies'

const SubTabButton = ({
    label,
    count,
    active,
    onPress,
    helpId,
}: {
    label: string
    count?: number
    active: boolean
    onPress: () => void
    helpId?: string
}) => (
    <XStack
        alignItems="center"
        gap="$2"
        paddingVertical={10}
        paddingHorizontal="$1"
        marginBottom={-1}
        borderBottomWidth={2}
        borderBottomColor={active ? '$primary' : 'transparent'}
        cursor="pointer"
        animation="quick"
        onPress={onPress}
        {...(helpId ? { 'data-help-id': helpId } : {})}
    >
        <SizableText
            size="$2"
            fontWeight="500"
            color={active ? '$color' : '$colorMuted'}
        >
            {label}
        </SizableText>
        {count != null && (
            <View
                paddingHorizontal={4}
                paddingVertical={0.5}
                borderRadius={999}
                backgroundColor={active ? '$accentSoft' : '$backgroundRaised'}
            >
                <SizableText
                    style={{ fontSize: 10 }}
                    fontWeight="600"
                    fontFamily="$mono"
                    color={active ? '$accentText' : '$colorMuted'}
                >
                    {count}
                </SizableText>
            </View>
        )}
    </XStack>
)

export default function UsersPage() {
    const { user } = useUserData()
    const usersApi = useUsersApi()
    const navigate = useNavigate()
    const [tab, setTab] = useSearchParamState<SubTab>('view', 'members')

    const canManage = user.permissions.includes('users.view')
    const canInvite = user.permissions.includes('users.edit')

    const tabs: { id: SubTab; label: string; count?: number; helpId: string }[] = [
        { id: 'members', label: 'Members', count: usersApi.data?.length, helpId: 'users-tab-members' },
        ...(canManage
            ? ([
                  { id: 'roles', label: 'Roles', helpId: 'users-tab-roles' },
                  { id: 'agencies', label: 'Conjunctional Agencies', helpId: 'users-tab-agencies' },
              ] as { id: SubTab; label: string; helpId: string }[])
            : []),
    ]

    const activeTab = tabs.some(t => t.id === tab) ? tab : 'members'

    return (
        <View
            paddingHorizontal="$6"
            $sm={{ paddingHorizontal: '$4' }}
            data-help-id="settings-users"
        >
            <SectionHead
                title="Users"
                subtitle="Manage your team, permissions, and conjunctional agencies."
                buttons={
                    canInvite ? (
                        <Button
                            theme="primary"
                            fontWeight="600"
                            borderRadius="$4"
                            icon={<Plus size={16} />}
                            onPress={() => navigate('new')}
                            data-help-id="users-invite-button"
                        >
                            Invite User
                        </Button>
                    ) : undefined
                }
            />

            <XStack
                borderBottomWidth={1}
                borderBottomColor="$borderColor"
                gap="$5"
                marginBottom="$5"
                data-help-id="users-subtabs"
            >
                {tabs.map(t => (
                    <SubTabButton
                        key={t.id}
                        label={t.label}
                        count={t.count}
                        active={activeTab === t.id}
                        onPress={() => setTab(t.id)}
                        helpId={t.helpId}
                    />
                ))}
            </XStack>

            {activeTab === 'members' && <MembersList />}
            {activeTab === 'roles' && canManage && <RolesPanel />}
            {activeTab === 'agencies' && canManage && <AgenciesPanel />}
        </View>
    )
}
